import { useCallback, useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'next-i18next'

import { fetchFacilitiesByPrefecture } from 'features/facility/services/facilitiesListApi'
import type { FacilitiesByPrefectureResponse } from 'features/facility/types/facility'

export type FetchFacilitiesOptions = {
  type?: 'facility' | 'station'
}

export const useFacilities = () => {
  const { i18n } = useTranslation()
  const [groupedFacilities, setGroupedFacilities] = useState<FacilitiesByPrefectureResponse | null>(
    null
  )
  const [isLoading, setLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const fetchFacilities = useCallback(
    async (prefectureId: number, options?: FetchFacilitiesOptions) => {
      setLoading(true)
      setIsError(false)

      try {
        const { data } = await fetchFacilitiesByPrefecture(
          prefectureId,
          i18n.language,
          options?.type
        )
        setGroupedFacilities(data)
      } catch (error) {
        setIsError(true)
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
          tags: { hook: 'useFacilities' },
          extra: {
            prefectureId,
            locale: i18n.language,
            type: options?.type,
          },
        })
      } finally {
        setLoading(false)
      }
    },
    [i18n.language]
  )

  return {
    groupedFacilities,
    isLoading,
    isError,
    fetchFacilities,
  }
}
